import React, { useState, useEffect } from 'react';
import { ImageIcon, ImageOff, Loader2 } from 'lucide-react';
import { getPublicImageUrl } from '../../../lib/webhookService';

interface PostImageProps {
  imageUrl: string | null;
  logoUrl?: string | null;
}

export const PostImage: React.FC<PostImageProps> = ({ imageUrl, logoUrl }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  useEffect(() => {
    let mounted = true;

    const loadImage = async () => {
      if (!imageUrl || imageUrl === 'Brak' || imageUrl.trim() === '') {
        if (mounted) {
          setDisplayUrl(null);
          setImageLoading(false);
        }
        return;
      }

      try {
        setImageError(false);
        setImageLoading(true);
        
        // Jeśli URL jest z domeny sklep.aderlo.pl, użyj go bezpośrednio
        if (imageUrl.includes('sklep.aderlo.pl')) {
          if (mounted) {
            setDisplayUrl(imageUrl);
            setImageError(false);
            setImageLoading(false);
          }
          return;
        }

        // Dla innych URLi, sprawdź czy są z Supabase
        if (imageUrl.includes('supabase.co')) {
          const publicUrl = await getPublicImageUrl(imageUrl);
          if (mounted && publicUrl) {
            setDisplayUrl(publicUrl);
            setImageError(false);
            setImageLoading(false);
            return;
          }
        }

        // Dla pozostałych URLi, użyj ich bezpośrednio
        if (mounted) {
          setDisplayUrl(imageUrl);
          setImageError(false);
          setImageLoading(false);
        }

      } catch (error) {
        console.error('Error loading image:', error);
        if (mounted) {
          setImageError(true);
          setImageLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      mounted = false;
    };
  }, [imageUrl]);

  const handleRetry = async () => {
    if (retryCount < maxRetries) {
      setImageError(false);
      setImageLoading(true);
      setRetryCount(prev => prev + 1);
      
      try {
        if (imageUrl?.includes('supabase.co')) {
          const publicUrl = await getPublicImageUrl(imageUrl);
          if (publicUrl) {
            setDisplayUrl(publicUrl);
            setImageError(false);
            setImageLoading(false);
            return;
          }
        }
        
        setDisplayUrl(imageUrl);
        setImageError(false);
        setImageLoading(false);
      } catch (error) {
        console.error('Error retrying image load:', error);
        setImageError(true);
        setImageLoading(false);
      }
    }
  };

  if (!imageUrl || imageUrl === 'Brak' || imageUrl.trim() === '') {
    return (
      <div className="w-full h-48 rounded-lg bg-gray-100 flex flex-col items-center justify-center text-gray-400">
        <ImageOff className="w-8 h-8 mb-2" />
        <p className="text-sm">No image available</p>
      </div>
    );
  }

  if (imageLoading) {
    return (
      <div className="w-full h-48 rounded-lg bg-gray-100 flex flex-col items-center justify-center text-gray-400">
        <Loader2 className="w-8 h-8 mb-2 animate-spin" />
        <p className="text-sm">Loading image...</p>
      </div>
    );
  }

  if (imageError || !displayUrl) {
    return (
      <div className="w-full h-48 rounded-lg bg-gray-100 flex flex-col items-center justify-center text-gray-400">
        <ImageOff className="w-8 h-8 mb-2" />
        <p className="text-sm">Failed to load image</p>
        <p className="text-xs text-gray-500 mt-1">{imageUrl}</p>
        {retryCount < maxRetries && (
          <button
            onClick={handleRetry}
            className="mt-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-700 hover:underline"
          >
            Retry ({maxRetries - retryCount} attempts left)
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative rounded-lg overflow-hidden bg-gray-100">
      <img
        src={displayUrl}
        alt="Post preview"
        className="w-full h-auto max-h-[500px] object-contain"
        onError={() => setImageError(true)}
      />
      {logoUrl && (
        <div className="absolute top-3 left-3 bg-white/75 rounded-md p-1.5 shadow-sm">
          <img
            src={logoUrl}
            alt="Logo overlay"
            className="h-8 w-auto object-contain"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </div>
      )}
    </div>
  );
};